import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'hidden',
    'input',
    'dropdown',
    'createToggle',
    'createPanel',
    'nameInput',
    'createBtn',
    'error',
  ]
  static values = { divisions: Array, createUrl: String }

  connect() {
    this._onCreated = this.#onDivisionCreated.bind(this)
    this._onCreateError = this.#onDivisionCreateError.bind(this)
    this.element.addEventListener('division:created', this._onCreated)
    this.element.addEventListener('division:create-error', this._onCreateError)

    if (!this.hasDropdownTarget) return

    this._dd = this.dropdownTarget
    this._container = this.element.closest('.modal-content') || document.body
    this._container.appendChild(this._dd)

    if (this.hasHiddenTarget && this.hasInputTarget) {
      const currentId = this.hiddenTarget.value
      if (currentId) {
        const match = this.divisionsValue.find(
          (d) => String(d.id) === String(currentId),
        )
        if (match) this.inputTarget.value = match.name
      }
    }
  }

  disconnect() {
    this.element.removeEventListener('division:created', this._onCreated)
    this.element.removeEventListener(
      'division:create-error',
      this._onCreateError,
    )
    this._dd?.remove()
  }

  showDropdown() {
    if (!this._dd) return
    const inputRect = this.inputTarget.getBoundingClientRect()
    const containerRect = this._container.getBoundingClientRect()
    const dd = this._dd
    dd.style.position = 'absolute'
    dd.style.top = `${inputRect.bottom - containerRect.top}px`
    dd.style.left = `${inputRect.left - containerRect.left}px`
    dd.style.width = `${inputRect.width}px`
    dd.style.right = 'auto'
    this.#render('')
    dd.style.display = 'block'
  }

  hideDropdown() {
    if (!this._dd) return
    this._dd.style.display = 'none'
  }

  filter() {
    if (!this._dd) return
    this.#render(this.inputTarget.value)
    if (this._dd.style.display === 'none') this.showDropdown()
  }

  handleOutsideClick(e) {
    if (!this._dd || this._dd.style.display === 'none') return
    if (!this.element.contains(e.target) && !this._dd.contains(e.target))
      this.hideDropdown()
  }

  selectOption(e) {
    e.preventDefault()
    const id = e.currentTarget.dataset.divisionId
    const name = e.currentTarget.dataset.divisionName
    this.hiddenTarget.value = id
    this.inputTarget.value = id ? name : ''
    this.hideDropdown()
  }

  hoverIn(e) {
    e.currentTarget.style.background = '#f5f5f5'
  }
  hoverOut(e) {
    e.currentTarget.style.background = ''
  }

  toggleCreate(e) {
    e.preventDefault()
    this.hideDropdown()
    this.createPanelTarget.style.display = 'block'
    this.createToggleTarget.style.display = 'none'
    this.nameInputTarget.focus()
  }

  cancelCreate() {
    this.createPanelTarget.style.display = 'none'
    this.createToggleTarget.style.display = 'inline-block'
    this.nameInputTarget.value = ''
    this.errorTarget.style.display = 'none'
    this.errorTarget.textContent = ''
    this.createBtnTarget.disabled = false
    this.createBtnTarget.textContent = 'Create'
  }

  async createDivision() {
    const name = this.nameInputTarget.value.trim()
    if (!name) {
      this.nameInputTarget.focus()
      return
    }
    this.createBtnTarget.disabled = true
    this.createBtnTarget.textContent = 'Creating…'

    try {
      const body = new URLSearchParams({
        'division[name]': name,
        source: 'invitation_inline',
      })
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.#csrfToken(),
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'text/javascript',
        },
        body: body.toString(),
      })
      const script = await response.text()

      ;(0, eval)(script)
    } catch {
      this.errorTarget.textContent = 'Something went wrong. Please try again.'
      this.errorTarget.style.display = 'block'
      this.createBtnTarget.disabled = false
      this.createBtnTarget.textContent = 'Create'
    }
  }

  nameInputKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      this.createDivision()
    }
  }

  #render(query) {
    const q = (query || '').toLowerCase().trim()
    const results = q
      ? this.divisionsValue
          .filter((d) => d.name.toLowerCase().includes(q))
          .sort((a, b) => {
            const aStart = a.name.toLowerCase().startsWith(q) ? 0 : 1
            const bStart = b.name.toLowerCase().startsWith(q) ? 0 : 1
            return aStart - bStart || a.name.localeCompare(b.name)
          })
      : this.divisionsValue

    this._dd.innerHTML = ''
    if (!q) this._dd.appendChild(this.#optionEl('', '-- Division --', true))
    results
      .slice(0, 300)
      .forEach((d) => this._dd.appendChild(this.#optionEl(d.id, d.name, false)))
  }

  #optionEl(id, name, muted) {
    const el = document.createElement('div')
    el.textContent = name
    el.dataset.divisionId = id
    el.dataset.divisionName = name
    el.style.cssText = `padding:6px 12px;cursor:pointer;${muted ? 'color:#999' : ''}`
    el.addEventListener('mousedown', (e) => this.selectOption(e))
    el.addEventListener('mouseenter', () => {
      el.style.background = '#f5f5f5'
    })
    el.addEventListener('mouseleave', () => {
      el.style.background = ''
    })
    return el
  }

  #csrfToken() {
    return (
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') ?? ''
    )
  }

  #onDivisionCreated({ detail: { id, name } }) {
    this.divisionsValue = [...this.divisionsValue, { id, name }]
    this.hiddenTarget.value = id
    this.inputTarget.value = name
    this.hideDropdown()
    if (this.hasCreatePanelTarget) this.cancelCreate()
  }

  #onDivisionCreateError({ detail: { message } }) {
    this.errorTarget.textContent = message
    this.errorTarget.style.display = 'block'
    this.createBtnTarget.disabled = false
    this.createBtnTarget.textContent = 'Create'
  }
}
